import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { useAuthHttp } from '../../hooks';
import { FLEET_GET, VEHICLE_UPDATE, FLEET_CATEGORIES } from '../../constants';
import { VehicleSummary as Vehicle } from '../FleetMaster/types';
import {
  VehicleForm,
  VehicleFormValues,
  VEHICLE_CATEGORIES,
  FUEL_TYPES,
  EMISSION_STANDARDS,
  AVERAGE_ANNUAL_MILEAGE_UNITS,
  DIMENSIONS_UNITS_OF_MEASURE,
  SPECIAL_EQUIPMENT,
  toOptionalNumber,
} from '../../forms/VehicleForm/VehicleForm';

export function EditVehicle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [initialValues, setInitialValues] =
    useState<Partial<VehicleFormValues> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useDocumentTitle(
    vehicle ? `FLOX - Edit ${vehicle.model}` : 'FLOX - Edit Vehicle'
  );

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Fleet', href: '/fleet-master', current: false },
    { name: 'Edit Vehicle', href: '#', current: true },
  ];

  const fetchVehicle = async () => {
    try {
      const { data } = await authHttp.post(FLEET_GET, {
        pagination: { itemsPerPage: 100 },
      });

      if (data?.vehicles) {
        const foundVehicle = data.vehicles.find(
          (v: Vehicle) => v.vehicleId === id
        );
        if (foundVehicle) {
          setVehicle(foundVehicle);

          // Map vehicle data to form values
          const fleetCategoryValue =
            FLEET_CATEGORIES.find((c) => c.value === foundVehicle.vehicleType)
              ?.value || FLEET_CATEGORIES[0].value;
          const vehicleCategory =
            VEHICLE_CATEGORIES.find(
              (c) => c.value === foundVehicle.vehicleCategory
            ) || VEHICLE_CATEGORIES[0];
          const fuelType =
            FUEL_TYPES.find((f) => f.value === foundVehicle.fuelType) ||
            FUEL_TYPES[0];
          const emissionStandard =
            EMISSION_STANDARDS.find(
              (e) => e.value === foundVehicle.emissionStandard
            ) || EMISSION_STANDARDS[0];
          const mileageUnits =
            AVERAGE_ANNUAL_MILEAGE_UNITS.find(
              (u) => u.value === foundVehicle.averageAnnualMileageUnits
            ) || AVERAGE_ANNUAL_MILEAGE_UNITS[0];
          const dimensionsUnit =
            DIMENSIONS_UNITS_OF_MEASURE.find(
              (u) =>
                u.value ===
                foundVehicle.vehicleCapacity?.maxCapacityDimensions
                  ?.dimensionsUnitOfMeasure
            ) || DIMENSIONS_UNITS_OF_MEASURE[3];
          const specialEquipment = (foundVehicle.specialEquipment || [])
            .map((eq: string) => SPECIAL_EQUIPMENT.find((s) => s.value === eq))
            .filter(Boolean) as { label: string; value: string }[];

          setInitialValues({
            model: foundVehicle.model || '',
            registrationNumber: foundVehicle.registrationNumber || '',
            vehicleType: fleetCategoryValue,
            vehicleCategory,
            specialEquipment,
            fuelType,
            emissionStandard,
            averageAnnualMileage: foundVehicle.averageAnnualMileage ?? '',
            averageAnnualMileageUnits: mileageUnits,
            maxPallets: foundVehicle.vehicleCapacity?.maxPallets ?? '',
            maxKilograms: foundVehicle.vehicleCapacity?.maxKilograms ?? '',
            width:
              foundVehicle.vehicleCapacity?.maxCapacityDimensions?.width ?? '',
            height:
              foundVehicle.vehicleCapacity?.maxCapacityDimensions?.height ?? '',
            length:
              foundVehicle.vehicleCapacity?.maxCapacityDimensions?.length ?? '',
            dimensionsUnitOfMeasure: dimensionsUnit,
          });
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleOnSubmit = async (data: VehicleFormValues) => {
    setIsSubmitting(true);
    try {
      const payload = {
        vehicle: {
          vehicleId: id,
          model: data.model,
          registrationNumber: data.registrationNumber,
          vehicleType: data.vehicleType,
          vehicleCategory: data.vehicleCategory.value,
          specialEquipment: data.specialEquipment.map((eq) => eq.value),
          vehicleCapacity: {
            maxCapacityDimensions: {
              width: toOptionalNumber(data.width),
              length: toOptionalNumber(data.length),
              height: toOptionalNumber(data.height),
              dimensionsUnitOfMeasure: data.dimensionsUnitOfMeasure.value,
            },
            maxPallets: toOptionalNumber(data.maxPallets),
            maxKilograms: toOptionalNumber(data.maxKilograms),
          },
          fuelType: data.fuelType.value,
          emissionStandard: data.emissionStandard.value,
          averageAnnualMileage: toOptionalNumber(data.averageAnnualMileage),
          averageAnnualMileageUnits: data.averageAnnualMileageUnits.value,
        },
      };

      await authHttpFormData.post(VEHICLE_UPDATE, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      navigate(`/vehicle/${id}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-gray-500'>Loading vehicle...</div>
      </div>
    );
  }

  if (!vehicle || !initialValues) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen gap-4'>
        <div className='text-gray-500'>Vehicle not found</div>
        <button
          type='button'
          onClick={() => navigate('/fleet-master')}
          className='px-4 py-2 bg-primary text-white rounded-lg'
        >
          Back to Fleet
        </button>
      </div>
    );
  }

  return (
    <VehicleForm
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      header={`Edit ${vehicle.model}`}
      pages={pages}
      initialValues={initialValues}
    />
  );
}
